<script setup lang="ts">
import { computed, ref } from 'vue'
import Button from 'primevue/button'
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/outline'
import type { DebugFocus } from '@shared/debugger/contract'
import { pageRules } from '@shared/targeting/pageConditions'
import type { TargetingCondition } from '@shared/targeting/pageConditions'
import { useConfigStore } from '@dashboard/stores/configStore'
import { useDomainNameStore } from '@dashboard/modules/settings/stores/domainNameStore'
import { useNotificationStore } from '@dashboard/stores/notificationStore'
import { openDebuggerSession } from '@dashboard/services/debuggerSession'
import { resolveRoverTargetUrl } from '@dashboard/services/roverUrl'
import { formatPreviewUrl } from '@dashboard/utils/previewUrl'

const props = defineProps<{
  focus?: DebugFocus
  url?: string
  conditions?: TargetingCondition | null
  label?: string
  variant?: 'primary' | 'secondary'
  /** Open the unpublished draft (the same preview parameters as the PublishBar preview), not the live config. */
  draft?: boolean
}>()
const emit = defineEmits<{ opened: [] }>()
const config = useConfigStore()
const domains = useDomainNameStore()
const notifications = useNotificationStore()
const opening = ref(false)
const target = computed(() => {
  const domain = domains.domains[0]
  const rule = props.conditions
    ? pageRules(props.conditions)?.find(
        (page) => page.path !== null && !page.path.includes('*') && (!page.domain || !page.domain.includes('*'))
      )
    : null
  const page = rule ? `${rule.domain || domain || ''}${rule.path?.startsWith('/') ? '' : '/'}${rule.path}` : undefined
  return resolveRoverTargetUrl(props.url || page || domain || '', domain)
})
async function launch() {
  if (!config.currentTeamPid || config.currentTeamPid === 'demo' || !target.value) {
    return
  }
  opening.value = true
  try {
    const url = props.draft ? (formatPreviewUrl(target.value) ?? target.value) : target.value
    await openDebuggerSession(config.currentTeamPid, url, undefined, props.focus)
    emit('opened')
  } catch (error) {
    notifications.addError(
      error instanceof Error ? error.message : 'Could not open the debugger. Please try again.',
      false
    )
  } finally {
    opening.value = false
  }
}
</script>
<template>
  <Button
    :label="label || 'Debug on site'"
    size="small"
    :severity="variant === 'primary' ? undefined : 'secondary'"
    :outlined="variant !== 'primary'"
    :loading="opening"
    :disabled="!target || !config.currentTeamPid || config.currentTeamPid === 'demo'"
    @click="launch">
    <template #icon><ArrowTopRightOnSquareIcon class="size-4" /></template>
  </Button>
</template>
